<template>
  <div class="card">
    <div class="card-header ps-7 pe-5">
      <h3 class="card-title align-items-start flex-column">
        {{ translate("Menu_Audit_Questions") }}
        <span class="text-muted mt-1 fw-bold fs-7"
          >{{ records }} {{ translate("Records_Found") }}</span
        >
      </h3>
    </div>

    <div class="p-5">
      <div>
        <div class="card-toolbar mb-5">
          <!--begin::Toolbar-->

          <div class="d-flex justify-content-end align-items-center">
            <div>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="router.push({ name: 'new-questions' })"
              >
                {{ translate("question_component_button_add_question") }}
              </button>
              <button
                v-show="selectedQuestions.length > 0"
                @click="deleteQuestion($event)"
                type="button"
                class="btn bg-light bg-hover-secondary btn-sm border position-relative ms-2"
              >
                {{ translate("Delete_Select")
                }}<span
                  class="position-absolute top-0 start-100 translate-middle badge badge-circle badge-danger"
                  >{{ selectedQuestions.length }}</span
                >
              </button>
            </div>
          </div>
        </div>
      </div>
      <DataTable
        :value="questionList"
        dataKey="Id"
        @select-all-change="onSelectAllChange"
        @sort="onSort($event)"
        @filter="onFilter($event)"
        @row-select="onRowSelect"
        @row-unselect="onRowUnselect"
        v-model:selection="selectedQuestions"
        v-model:filters="filters"
        filterDisplay="row"
        :globalFilterFields="['code', 'categoryIds', 'questionText', 'status']"
        :lazy="true"
        :paginator="false"
        :loading="loading"
        :totalRecords="totalRecords"
        :selectAll="selectAll"
        removableSort
        class="rounded"
        responsiveLayout="scroll"
      >
        <Column
          selectionMode="multiple"
          headerStyle="width: 3em"
        ></Column>
        <Column
          field="Code"
          :header="translate('location_list_column_code')"
          filterField="code"
          filterMatchMode="contains"
          ref="code"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              name="code"
              type="text"
              @keydown.enter="filterCallback()"
              class="p-column-filter"
              :placeholder="translate('Search')"
              @keyup="inputNull($event)"
            />
          </template>
        </Column>

        <Column
          field="QuestionText"
          :header="translate('question_component_column_title2')"
          filterField="questionText"
          filterMatchMode="contains"
          ref="questionText"
          :sortable="true"
          style="max-width: 500px"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              name="questionText"
              type="text"
              @keydown.enter="filterCallback()"
              class="p-column-filter"
              :placeholder="translate('Search')"
              @keyup="inputNull($event)"
            />
          </template>
        </Column>

        <Column
          field="CategoryName"
          :header="translate('question_component_column_category')"
          filterField="categoryIds"
          :showFilterMenu="false"
          style="min-width: 14rem"
          filterMatchMode="contains"
          ref="categoryIds"
          :sortable="true"
        >
          <template #body="{ data }">
            <span class="image-text">{{ data.CategoryName }}</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              @change="filterCallback()"
              :options="categoryList"
              optionLabel="Value"
              optionValue="Id"
              :placeholder="translate('Search')"
              class="p-column-filter"
            >
              <template #option="slotProps">
                <div class="p-multiselect-representative-option">
                  <span class="image-text">{{ slotProps.option.Value }}</span>
                </div>
              </template>
            </MultiSelect>
          </template>
        </Column>

        <Column
          field="status"
          :header="translate('question_component_column_info')"
          dataType="boolean"
          filterField="status"
          filterMatchMode="contains"
          style="width: 6rem; text-align: center"
          class="p-title-status"
          filterMenuStyle="text-align:center"
          :showClearButton="false"
        >
          <template #body="{ data }">
            <a
              :title="`${
                data.InfoOnly
                  ? translate('checklist_create_modal_checkbox_label')
                  : translate('question_component_name')
              }`"
            >
              <i
                class="bi"
                :class="{
                  'bi bi-info-circle text-primary fs-2': data.InfoOnly,
                  'bi bi-question-circle text-gray-700 fs-2 ms-1':
                    !data.InfoOnly,
                }"
              ></i>
            </a>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <TriStateCheckbox
              v-model="filterModel.value"
              @change="filterCallback()"
              style="margin-left: 3px"
            />
          </template>
        </Column>

        <Column
          field="Id"
          :rowEditor="true"
          bodyStyle="text-align:center"
        >
          <template #body="{ data }">
            <div class="d-flex justify-content-end">
              <a
                class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                :title="translate('question_list_copy_button')"
                @click="copyQuestion(data.Id)"
              >
                <span class="svg-icon svg-icon-3">
                  <inline-svg src="media/icons/duotune/general/gen054.svg" />
                </span>
              </a>
              <router-link
                class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                :title="translate('Update')"
                :to="`/questions/edit-questions/${data.Id}`"
              >
                <span class="svg-icon svg-icon-3">
                  <inline-svg src="media/icons/duotune/art/art005.svg" />
                </span>
              </router-link>

              <a
                class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
                :title="translate('Delete')"
                @click="deleteQuestion($event, data)"
              >
                <span class="svg-icon svg-icon-3">
                  <inline-svg src="media/icons/duotune/general/gen027.svg" />
                </span>
              </a>
            </div>
          </template>
        </Column>
      </DataTable>
      <ConfirmPopup></ConfirmPopup>
      <div class="mt-5">
        <Pagination
          @onPage="onPage"
          :TotalData="records"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useConfirm } from "primevue/useconfirm";

//Store Tanımlaması
const store = useStore();
const confirm = useConfirm();
//Router Tanımlaması
const router = useRouter();

// Multi Language
const { t, te, locale } = useI18n();
const translate = (text) => {
  if (te(text)) {
    return t(text);
  } else {
    return text;
  }
};

//Değişkenler
const questionList = computed(
  () => store.getters["QuestionModule/_getQuestions"]
);

let selectedQuestions = ref([]);
const selectAll = ref(false);
const totalRecords = ref(0);
const lazyParams = ref({});
const loading = computed(() => store.getters["QuestionModule/_getLoading"]);
const records = computed(
  () => store.getters["QuestionModule/_getTotalRecords"]
);

const categoryList = ref([]);

//Data Table Filter Tanımlaması
const filters = ref({
  code: { value: null, matchMode: "contains" },
  categoryIds: { value: [], matchMode: "contains" },
  questionText: { value: null, matchMode: "contains" },
  status: { value: null, matchMode: "contains" },
});

// Data Table Eventları
const onPage = (e) => {
  lazyParams.value.page = e.step.value;
  lazyParams.value.pageSize = e.pageSize.value;
  getData(lazyParams.value);
};

const onSort = (event) => {
  lazyParams.value.sortColumn = event.sortField;
  lazyParams.value.sortDirection = event.sortOrder === 1 ? "asc" : "desc";
  getData(lazyParams.value);
};

const onFilter = () => {
  lazyParams.value.code = filters.value.code.value;

  lazyParams.value.questionText = filters.value.questionText.value;

  let categoryIds = filters.value.categoryIds.value;
  lazyParams.value.categoryIds = categoryIds ? categoryIds.map(Number) : [];

  if (filters.value.status.value === null) {
    lazyParams.value.infoStatus = 0;
  } else {
    lazyParams.value.infoStatus = filters.value.status.value === true ? 1 : 2;
  }
  getData(lazyParams.value);
};

const onSelectAllChange = (event) => {
  if (event.checked) {
    selectAll.value = true;
    selectedQuestions.value = questionList.value;
  } else {
    selectAll.value = false;
    selectedQuestions.value = [];
  }
};

const onRowSelect = () => {
  selectAll.value = selectedQuestions.value.length === totalRecords.value;
};

const onRowUnselect = () => {
  selectAll.value = false;
};

const inputNull = (event) => {
  if (event.target.value === "") {
    lazyParams.value[event.target.name] = "";
    getData(lazyParams.value);
  }
};

// Customer Delete İşlemi
const deleteQuestion = (event, Item) => {
  let obj = [];
  let list = [];
  if (Item !== undefined) {
    obj.push(Item.Id);
    list.push(obj);
    list.push(lazyParams.value);
  } else {
    selectedQuestions.value.forEach((item) => {
      obj.push(item.Id);
    });
    list.push(obj);
    list.push(lazyParams.value);
  }
  let message = ref("");
  if (obj.length > 1) {
    message.value = translate("confirm_message_questions");
  } else {
    message.value = translate("confirm_message_question");
  }
  confirm.require({
    target: event.currentTarget,
    message: message.value,
    icon: "pi pi-exclamation-triangle",
    acceptLabel: translate("customer_location_confirm_acceptLabel"),
    rejectLabel: translate("customer_location_confirm_rejectLabel"),
    acceptClass: "btn btn-sm btn-primary",
    rejectClass: "btn btn-sm btn-light",
    accept: () => {
      store
        .dispatch("QuestionModule/DELETE_QUESTIONS", list)
        .finally((selectedQuestions.value = []));
    },
    reject: () => {
      return;
    },
  });
};

//Data Çekme Metodo
const getData = (payLoad) => {
  store.dispatch("QuestionModule/GET_ALL_QUESTIONS", payLoad);
};

store.dispatch("CategoryModule/GET_COMMON_CATEGORY").then((Response) => {
  categoryList.value = Response;
});

watch(locale, () => {
  setCurrentPageBreadcrumbs(translate("question_component_breadcrumbs_list"), [
    translate("question_component_breadcrumbs"),
  ]);
});

onMounted(() => {
  lazyParams.value = {
    page: 1,
    pageSize: 10,
    sortColumn: null,
    sortDirection: null,
    questionText: null,
    code: null,
    categoryIds: [],

    infoStatus: 0,
  };
  setCurrentPageBreadcrumbs(translate("question_component_breadcrumbs_list"), [
    translate("question_component_breadcrumbs"),
  ]);
});

const copyQuestion = (Id) => {
  store.dispatch("QuestionModule/QUESTION_COPY", Id).then((respone) => {
    router.push(`/questions/edit-questions/${respone.Data.id}`);
  });
};
getData({
  page: 1,
  pageSize: 10,
});
</script>
